/**
 * ExpenseConflictResolver — HITL UI for duplicate-gasto resolution
 * ─────────────────────────────────────────────────────────────────
 * Renders an amber banner (one card per `grupo_conflicto_id`) listing all
 * candidate gastos and a RadioGroup so the user picks the authoritative one.
 * Confirming flips `aplica_costeo_inventario` server-side via the
 * `useResolveConflict` mutation, then the landed-cost engine recalculates.
 */
import { useState, useMemo } from "react";
import { AlertTriangle, FileText, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  useExpenseConflicts,
  useResolveConflict,
  type ConflictGroup,
} from "@/hooks/useExpenseConflicts";

interface Props {
  embarqueId: string | null | undefined;
  isLiquidado?: boolean;
}

const FUENTE_LABEL: Record<string, string> = {
  BL: "Bill of Lading",
  DIM: "Declaración (DIM)",
  FACTURA: "Factura",
  MANUAL: "Manual",
  OCR: "OCR / IA",
};

function formatMonto(g: ConflictGroup["candidates"][number]) {
  if (g.moneda === "COP") {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(g.monto);
  }
  return `${g.moneda} ${g.monto.toLocaleString()}`;
}

function ConflictCard({
  group,
  embarqueId,
  disabled,
}: {
  group: ConflictGroup;
  embarqueId: string;
  disabled: boolean;
}) {
  const resolve = useResolveConflict();
  const [pick, setPick] = useState<string>(
    group.selectedId ?? group.candidates[0]?.id ?? "",
  );

  const handleConfirm = async () => {
    if (!pick) return;
    try {
      await resolve.mutateAsync({
        groupId: group.groupId,
        winnerId: pick,
        embarqueId,
      });
      toast.success("Conflicto resuelto. Recalculando costos…");
    } catch (e: any) {
      toast.error("No se pudo resolver el conflicto", {
        description: e?.message ?? "Error desconocido",
      });
    }
  };

  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant="outline" className="text-[10px] h-5 border-amber-500/60 text-amber-700 dark:text-amber-300">
            {group.categoria ?? "Categoría sin definir"}
          </Badge>
          <span className="text-[11px] text-amber-700/90 dark:text-amber-300/90">
            {group.candidates.length} gastos detectados — elige el válido
          </span>
        </div>
      </div>

      <RadioGroup value={pick} onValueChange={setPick} disabled={disabled || resolve.isPending}>
        <div className="space-y-1.5">
          {group.candidates.map((c) => {
            const fuenteLabel = FUENTE_LABEL[c.fuente ?? "MANUAL"] ?? c.fuente ?? "—";
            const conf = c.confianza_ocr != null ? Math.round(c.confianza_ocr * 100) : null;
            return (
              <Label
                key={c.id}
                htmlFor={`conf-${c.id}`}
                className={`flex items-center gap-3 rounded-md border bg-card px-2.5 py-2 cursor-pointer transition-colors ${
                  pick === c.id ? "border-amber-500/70 bg-amber-500/10" : "border-border hover:bg-muted/40"
                }`}
              >
                <RadioGroupItem id={`conf-${c.id}`} value={c.id} />
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <span className="text-xs font-medium truncate">{c.concepto}</span>
                  <Badge variant="secondary" className="text-[10px] h-5 gap-1 shrink-0">
                    <FileText className="h-2.5 w-2.5" />
                    {fuenteLabel}
                  </Badge>
                  {conf != null && (
                    <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                      OCR {conf}%
                    </span>
                  )}
                </div>
                <span className="text-xs font-semibold tabular-nums text-foreground shrink-0">
                  {formatMonto(c)}
                </span>
              </Label>
            );
          })}
        </div>
      </RadioGroup>

      <div className="flex justify-end">
        <Button
          size="sm"
          variant="default"
          className="h-7 text-xs"
          onClick={handleConfirm}
          disabled={disabled || resolve.isPending || !pick || pick === group.selectedId}
        >
          {resolve.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : null}
          Confirmar gasto válido
        </Button>
      </div>
    </div>
  );
}

export function ExpenseConflictResolver({ embarqueId, isLiquidado }: Props) {
  const { groups, hasUnresolved, isLoading } = useExpenseConflicts(embarqueId);

  const visible = useMemo(() => groups.filter((g) => g.unresolved), [groups]);

  if (!embarqueId || isLoading || visible.length === 0) return null;

  return (
    <Alert variant="default" className="mb-3 border-amber-500/50 bg-amber-500/10">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertTitle className="text-xs font-semibold text-amber-700 dark:text-amber-400">
        ⚠️ Conflicto de gastos detectado ({visible.length} {visible.length === 1 ? "grupo" : "grupos"})
      </AlertTitle>
      <AlertDescription className="text-[11px] text-amber-700/90 dark:text-amber-400/90 space-y-2">
        <p>
          El motor encontró gastos duplicados (por ejemplo, flete declarado en el BL y en la Casilla 64
          de la DIM). Para evitar la doble contabilización, indica cuál es el monto válido para cada
          categoría.
        </p>
        <div className="space-y-2">
          {visible.map((g) => (
            <ConflictCard
              key={g.groupId}
              group={g}
              embarqueId={embarqueId}
              disabled={!!isLiquidado}
            />
          ))}
        </div>
      </AlertDescription>
    </Alert>
  );
}
